import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API_BASE_URL } from '../../../app.definitions';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import {
  SubmissionActivityItem,
  WeightActivityItem,
  MealActivityItem,
  PendingInvitationRow,
  InteractionMetricsRow,
  EventLogRow,
} from '../actions/admin-statistics.actions';

type DailyMetricsDoc = {
  id: string;
  date: string;
  counters: Record<string, number>;
  createdAt: string;
  updatedAt: string;
};

@Injectable()
export class AdminStatisticsApiService {
  constructor(@Inject(API_BASE_URL) private apiBaseUrl: string, private http: HttpClient) {}

  getAllSubmissionActivity(dateFrom?: Date, dateTo?: Date): Observable<SubmissionActivityItem[]> {
    let params = new HttpParams();
    if (dateFrom) params = params.set('dateFrom', dateFrom.toISOString());
    if (dateTo) params = params.set('dateTo', dateTo.toISOString());

    return this.http
      .get<{ data: { submissions: SubmissionActivityItem[] } }>(
        `${this.apiBaseUrl}/users/me/submissions/activity`,
        { params }
      )
      .pipe(map((res) => res?.data?.submissions ?? []));
  }

  getAllWeightActivity(startDate?: Date, endDate?: Date): Observable<WeightActivityItem[]> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());

    return this.http
      .get<{ data: { weightEntries: WeightActivityItem[] } }>(
        `${this.apiBaseUrl}/users/me/weight-data/activity`,
        { params }
      )
      .pipe(map((res) => res?.data?.weightEntries ?? []));
  }

  getAllMealActivity(startDate?: Date, endDate?: Date): Observable<MealActivityItem[]> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());

    return this.http
      .get<{ data: { mealEntries: MealActivityItem[] } }>(
        `${this.apiBaseUrl}/users/me/nutrition-diary/activity`,
        { params }
      )
      .pipe(map((res) => res?.data?.mealEntries ?? []));
  }

  getInteractionMetrics(): Observable<InteractionMetricsRow[]> {
    return this.http
      .get<{ data: DailyMetricsDoc[] | { counters: Record<string, number> } }>(
        `${this.apiBaseUrl}/metrics/me`
      )
      .pipe(
        map((res) => {
          const data = res?.data as
            | DailyMetricsDoc[]
            | { counters: Record<string, number> }
            | undefined;
          if (Array.isArray(data)) {
            return data as InteractionMetricsRow[];
          }
          if (data && typeof data === 'object' && 'counters' in data) {
            const now = new Date();
            const isoDateOnly = now.toISOString().slice(0, 10);
            return [
              {
                id: 'synthetic',
                date: isoDateOnly,
                counters: data.counters ?? {},
                createdAt: now.toISOString(),
                updatedAt: now.toISOString(),
              },
            ] as InteractionMetricsRow[];
          }
          return [];
        })
      );
  }

  getAllPendingInvitations(): Observable<PendingInvitationRow[]> {
    return this.http
      .get<{
        data: Array<{
          advisorId: string;
          advisorEmail: string | null;
          advisorName: string;
          userId: string;
          userEmail: string | null;
          userName: string;
          status: 'pending';
        }>;
      }>(`${this.apiBaseUrl}/metrics/me/pending-invitations`)
      .pipe(map((res) => res?.data ?? []));
  }

  getEventLogs(): Observable<EventLogRow[]> {
    return this.http
      .get<{ data: { items: EventLogRow[]; total: number; skip: number; limit: number } }>(
        `${this.apiBaseUrl}/metrics/me/logs`
      )
      .pipe(map((res) => res?.data?.items ?? []));
  }
}
